import { z } from 'zod';
/**
 * Contrats de la ROTATION de la sélection (outil « Pivoter »). Source unique front + back.
 *
 * L'action « Pivoter » fait tourner des IMAGES en écrivant SEULEMENT le tag EXIF
 * `Orientation` de chaque original (rotation RELATIVE composée sur l'orientation
 * courante — cf. `apps/api/src/exif/orientation.ts`). Les PIXELS ne sont JAMAIS touchés
 * → l'`identity_hash` (empreinte pixels) reste IDENTIQUE : aucune ré-indexation, aucun
 * faux doublon. Le handler régénère les vignettes (sharp `.rotate()` réapplique la
 * nouvelle orientation) et recale `width`/`height`. Les VIDÉOS sont EXCLUES (comptées
 * « ignorées »).
 *
 * L'écriture sur /library est gardée par `LIBRARY_WRITABLE` (409 WRITE_DISABLED sinon,
 * comme la compression / l'EXIF / le déplacement). La rotation s'exécute en job
 * asynchrone (suivi dans la cloche). Une sauvegarde `_original` est OPTIONNELLE.
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : ce schéma de REQUÊTE passe par
 * `z.toJSONSchema(schema, { target: 'draft-7' })` au démarrage (enregistrement des routes
 * Fastify). Un `.transform()` PERSONNALISÉ y LÈVE → l'API plante en boucle. On n'en met
 * AUCUN (uniquement `.array`/`.enum`/`.boolean`/`.default` — représentables). Garde
 * anti-régression : `route-json-schema.test.ts`.
 */
// ---------------------------------------------------------------------------
// Rotation de la sélection (POST /api/libraries/:id/rotate)
// ---------------------------------------------------------------------------
/**
 * Sens de rotation demandé (rotation RELATIVE, composée sur l'orientation actuelle) :
 *  - `cw`   : quart de tour vers la DROITE (90° horaire).
 *  - `ccw`  : quart de tour vers la GAUCHE (90° antihoraire).
 *  - `flip` : demi-tour (180°).
 */
export const rotateDirectionSchema = z.enum(['cw', 'ccw', 'flip']);
/**
 * Corps de `POST /api/libraries/:id/rotate`. `assetIds` : la sélection à pivoter (≥ 1 ;
 * les non-images sont ignorées). `direction` : le sens (cw/ccw/flip). `keepOriginal`
 * (défaut vrai) garde une sauvegarde `<nom>_original` avant écriture. `dryRun` (défaut
 * faux) : APERÇU (compte les cibles sans rien écrire).
 */
export const rotateRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
    direction: rotateDirectionSchema,
    keepOriginal: z.boolean().default(true),
    dryRun: z.boolean().default(false),
});
/**
 * Ventilation d'un job `rotate`. Chaque asset ciblé tombe dans une catégorie :
 *  - `rotated` : images réellement pivotées (en dry-run : celles qui l'auraient été).
 *  - `skipped` : non-image (vidéo/format non géré) — jamais d'écriture.
 *  - `failed`  : erreur (ExifTool/relecture/chemin refusé) — non pivoté.
 */
export const rotateJobCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    rotated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=rotate.js.map